import { z } from 'zod';

import { zFirestampNow, zFirestampOptional } from '@elara/firebolt/zod-types';

/**
 * An enum to distinguish between a human user and an AI agent participant in a chat.
 * @private
 */
export const ParticipantType = z.enum(['user', 'agent']).describe('Whether this is a human user or AI agent');

/**
 * A Zod schema defining the structure of a participant in a chat session.
 * This can represent either a user or an AI agent.
 */
export const ParticipantSchema = z.object({
  // Identity
  id: z.string().describe('Unique identifier for the participant'),
  type: ParticipantType,
  name: z.string().describe('Display name of the participant'),
  photoURL: z.string().optional().describe('URL to avatar image'),

  // Permissions and role
  role: z
    .enum(['owner', 'collaborator', 'viewer'])
    .default('collaborator')
    .describe('Permission level within the chat'),

  // Status and activity
  status: z.enum(['online', 'away', 'offline']).default('offline').describe('Current availability status'),
  lastSeenAt: zFirestampOptional().describe('When the participant was last active'),

  // Metadata
  joinedAt: zFirestampNow().describe('When the participant joined the chat'),
  settings: z
    .object({
      notifications: z.boolean().default(true),
      showTypingIndicator: z.boolean().default(true),
      theme: z.enum(['light', 'dark', 'system']).default('system'),
    })
    .default({ notifications: true, showTypingIndicator: true, theme: 'system' })
    .optional()
    .describe('Participant-specific settings for this chat'),

  // Agent-specific fields (client-safe only)
  agentMetadata: z
    .object({
      capabilities: z.array(z.string()).optional().describe('List of agent capabilities like chat, search, booking'),
      preferredLanguage: z.string().default('en').optional(),
      responseStyle: z.enum(['concise', 'detailed', 'friendly']).default('friendly').optional(),
    })
    .optional()
    .describe('Client-safe metadata for AI agents'),
});

export type ParticipantSchema = typeof ParticipantSchema;

/**
 * The TypeScript type for a chat participant.
 */
export type Participant = z.output<ParticipantSchema>;

export type ParticipantInput = z.input<ParticipantSchema>;
